import { List } from "@material-ui/core";
import React from "react";

import { useAuth } from "@context/auth/AuthContext";
import { useUserState } from "@context/UserContext";
import { useFlags } from "flagsmith/react";

import { TypesPermissions } from "@components/Routes/TypesPermissions";
import { LegacyTypesPermissions } from "@components/Routes/legacyTypesPermissions";
import ROUTES from "@config/routes";
import { generatePermissionMatcher } from "@utils/index";
import {
  checkDriverOrContactPermission,
  isDriverOnlyProfile,
} from "@utils/permissions/permissionChecks";
import { getAdaptivePermissionMatcher } from "@utils/permissions/getAdaptivePermissionMatcher";
import { SIDEBAR_USER_FLAGS } from "@utils/featureFlags";
import { useSystemModulesAuthFlag } from "@hooks/useSystemModulesAuthFlag";

import {
  // AnnouncementsIcon,
  // AnnouncementsIconActive,
  CampaignMetricsIcon,
  CampaignMetricsIconActive,
  ColetaIcon,
  ColetaIconActive,
  ContactUsIcon,
  ContactUsIconActive,
  Driver,
  DriverActive,
  EmployeesIcon,
  EmployeesIconActive,
  GoalsIcon,
  GoalsIconActive,
  HomeIcon,
  HomeIconActive,
  PriceIcon,
  PriceIconActive,
  RoadActive,
  RoadLoadIcon,
  SalesIcon,
  SalesIconActive,
} from "@components/Icons";
import ShoppingCartOutlinedIcon from "@material-ui/icons/ShoppingCartOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import { Roles } from "@shared/model";
import { ISidebarItems } from "../model/ISidebarItems";
import useStylesSidebar from "../model/useStylesSidebar";
import SidebarMenuItem from "./SidebarMenuItem";

interface ListDrawerUserProps {
  sidebarOpen?: boolean;
}

const ListDrawerUser: React.FC<ListDrawerUserProps> = ({
  sidebarOpen = false,
}) => {
  const classes = useStylesSidebar();
  const userState = useUserState();
  const { permissionSet } = useAuth();
  const flags = useFlags(SIDEBAR_USER_FLAGS);
  const { flag_for_access_management } = useFlags([
    "flag_for_access_management",
  ]);
  const useSystemModulesAuth = useSystemModulesAuthFlag();

  const adaptivePermissionMatcher = (
    v2Permission: string,
    legacyPermission?: string
  ) =>
    getAdaptivePermissionMatcher(
      v2Permission,
      legacyPermission,
      useSystemModulesAuth
    );

  const contactUsPermissionMatcher = (): ((
    permissionRef: string
  ) => boolean) => {
    if (useSystemModulesAuth) {
      return generatePermissionMatcher(TypesPermissions.SAC);
    }

    return (_permissionRef: string) =>
      checkDriverOrContactPermission(
        permissionSet.SYSTEM_MODULES,
        permissionSet.ROLES
      );
  };

  const menuListUser: ISidebarItems[] = [
    {
      icon: <HomeIcon className={classes.icon} />,
      iconActive: <HomeIconActive className={classes.icon} />,
      text: "Início",
      link: ROUTES.USER_ROUTES.HOME,
      flag: "user_sidebar_home_user",
      hasPermission: generatePermissionMatcher(TypesPermissions.HOME),
    },
    {
      icon: (
        <ShoppingCartOutlinedIcon
          className={classes.icon}
          style={{ fontSize: 20 }}
        />
      ),
      iconActive: (
        <ShoppingCartOutlinedIcon
          className={classes.icon}
          style={{ fontSize: 20 }}
        />
      ),
      text: "Novo Pedido",
      link: ROUTES.USER_ROUTES.NEW_ORDER,
      flag: "user_sidebar_new_order",
      hasPermission: generatePermissionMatcher(TypesPermissions.ORDER),
    },
    {
      icon: (
        <InventoryOutlinedIcon
          className={classes.icon}
          sx={{ strokeWidth: 1, stroke: "white" }}
        />
      ),
      iconActive: (
        <InventoryOutlinedIcon
          className={classes.icon}
          sx={{ strokeWidth: 1, stroke: "white" }}
        />
      ),
      text: "Meus Pedidos",
      link: ROUTES.USER_ROUTES.MY_ORDERS,
      flag: "user_home_card_your_orders",
      hasPermission: generatePermissionMatcher(TypesPermissions.ORDER),
    },
    {
      icon: (
        <ReceiptLongOutlinedIcon
          className={classes.icon}
          sx={{ strokeWidth: 1, stroke: "white" }}
        />
      ),
      iconActive: (
        <ReceiptLongOutlinedIcon
          className={classes.icon}
          sx={{ strokeWidth: 1, stroke: "white" }}
        />
      ),
      text: "Consultar Títulos",
      link: ROUTES.USER_ROUTES.APPOINTMENT_TITLE,
      flag: "user_sidebar_appointment_title",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.TITLES,
        LegacyTypesPermissions.APPOINTMENTS_TITLE
      ),
    },
    {
      icon: (
        <DescriptionOutlinedIcon
          className={classes.icon}
          sx={{ strokeWidth: 1, stroke: "white" }}
        />
      ),
      iconActive: (
        <DescriptionOutlinedIcon
          className={classes.icon}
          sx={{ strokeWidth: 1, stroke: "white" }}
        />
      ),
      text: "Consultar Notas Fiscais",
      link: ROUTES.USER_ROUTES.APPOINTMENT_INVOICES,
      flag: "user_home_card_consult_fiscal_notes",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.INVOICES,
        LegacyTypesPermissions.APPOINTMENTS_INVOICES
      ),
    },
    {
      icon: <PriceIcon className={classes.icon} />,
      iconActive: <PriceIconActive className={classes.icon} />,
      text: "Preços de produtos ATEM",
      link: ROUTES.USER_ROUTES.APPOINTMENT_PRICE,
      flag: "user_sidebar_price_product_atem",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.PRODUCT_PRICES,
        LegacyTypesPermissions.PRODUCT_PRICE
      ),
    },
    {
      icon: <Driver className={classes.icon} />,
      iconActive: <DriverActive className={classes.icon} />,
      text: "Meus motoristas",
      link: ROUTES.USER_ROUTES.MY_DRIVERS,
      flag: "user_sidebar_my_drivers",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.DRIVER,
        LegacyTypesPermissions.PORTAL_CLIENTE
      ),
    },
    {
      icon: <RoadLoadIcon className={classes.icon} />,
      iconActive: <RoadActive className={classes.icon} />,
      text: "Agenda e Vem",
      link: ROUTES.USER_ROUTES.ROADLOAD,
      flag: "user_sidebar_road_load",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.APPOINTMENT,
        LegacyTypesPermissions.DRIVER
      ),
      onClick: () => {
        sessionStorage.setItem("sheetRoadLoad", JSON.stringify(false));
      },
    },
    // {
    //   icon: <SimulationIcon className={classes.icon} />,
    //   iconActive: <SimulationIconActive className={classes.icon} />,
    //   text: "Simular carregamento",
    //   link: ROUTES.USER_ROUTES.SIMULATE_ORDER,
    //   flag: "user_sidebar_hide_simulate_loading",
    //   hasPermission: generatePermissionMatcher(TypesPermissions.SIMULATION),
    // },
    {
      icon: <ContactUsIcon className={classes.icon} />,
      iconActive: <ContactUsIconActive className={classes.icon} />,
      text: "Fale conosco",
      link: ROUTES.USER_ROUTES.RECLAMATION_CONSULT,
      flag: "user_sidebar_contact_us",
      hasPermission: contactUsPermissionMatcher(),
    },
    {
      icon: <CampaignMetricsIcon width={24} height={24} />,
      iconActive: <CampaignMetricsIconActive className={classes.icon} />,
      text: "Métricas de campanhas",
      link: ROUTES.USER_ROUTES.DASH_POSH,
      flag: "user_sidebar_metrics_and_campaigns",
      hasPermission: generatePermissionMatcher(
        TypesPermissions.CAMPAIGN_METRICS
      ),
    },
    {
      icon: <GoalsIcon className={classes.icon} />,
      iconActive: <GoalsIconActive className={classes.icon} />,
      text: "Gestão de desempenho",
      link: ROUTES.USER_ROUTES.PERFORMANCE_MANAGEMENT,
      flag: "user_sidebar_hide_performance_management",
      hasPermission: generatePermissionMatcher(
        TypesPermissions.PERFORMANCE_MANAGEMENT
      ),
    },
    {
      icon: <EmployeesIcon className={classes.icon} />,
      iconActive: <EmployeesIconActive className={classes.icon} />,
      text: "Funcionários",
      link: ROUTES.USER_ROUTES.EMPLOYEE,
      flag: "user_sidebar_employee",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.EMPLOYEE,
        LegacyTypesPermissions.EMPLOYEE
      ),
    },
    {
      icon: <ColetaIcon className={classes.icon} />,
      iconActive: <ColetaIconActive className={classes.icon} />,
      text: "Bico e Produto",
      link: ROUTES.USER_ROUTES.NOZZLE,
      flag: "user_sidebar_product_nozzle",
      hasPermission: generatePermissionMatcher(TypesPermissions.NOZZLES),
    },
    {
      icon: <SalesIcon className={classes.icon} />,
      iconActive: <SalesIconActive className={classes.icon} />,
      text: "Suas vendas",
      link: ROUTES.USER_ROUTES.SALES,
      flag: "user_sidebar_your_sales",
      hasPermission: adaptivePermissionMatcher(
        TypesPermissions.COLETA,
        LegacyTypesPermissions.COLETA
      ),
    },
    {
      icon: <GoalsIcon className={classes.icon} />,
      iconActive: <GoalsIconActive className={classes.icon} />,
      text: "Desconto",
      link: ROUTES.USER_ROUTES.DISCOUNT,
      flag: "user_sidebar_hide_discount",
      hasPermission: generatePermissionMatcher(TypesPermissions.DISCOUNT),
    },
    {
      icon: <GoalsIcon className={classes.icon} />,
      iconActive: <GoalsIconActive className={classes.icon} />,
      text: "Metas e comissões",
      link: ROUTES.USER_ROUTES.GOALS_AND_COMMISSIONS,
      flag: "user_sidebar_goal_and_commissions",
      hasPermission: generatePermissionMatcher(
        TypesPermissions.GOALS_AND_COMMISSIONS
      ),
    },
    // {
    //   icon: <AnnouncementsIcon className={classes.icon} />,
    //   iconActive: <AnnouncementsIconActive className={classes.icon} />,
    //   text: "Comunicados",
    //   link: ROUTES.USER_ROUTES.HANDOUTS,
    //   flag: "user_sidebar_communique",
    //   hasPermission: generatePermissionMatcher(TypesPermissions.ANNOUNCEMENTS),
    // },
    {
      icon: <EmployeesIcon className={classes.icon} />,
      iconActive: <EmployeesIconActive className={classes.icon} />,
      text: "Ir para modo Admin",
      link: ROUTES.ADMIN_ROUTES.HOME,
      flag: "user_sidebar_sidebar_redirect_admin",
      render: () => userState?.adminMode,
    },
  ];

  const filteredMenuListUser = menuListUser.filter((item) => {
    const isAdmin =
      permissionSet?.ROLES.includes(Roles.ADMIN) ||
      permissionSet?.ROLES.includes(Roles.ADMINPORTAL);

    if (isAdmin) return flags[item.flag]?.enabled;

    // SP-1315: oculta "Meus motoristas" para perfil motorista puro (V2),
    // independentemente da flag flag_for_access_management. carrier tem
    // precedência e mantém o item visível.
    if (
      item.link === ROUTES.USER_ROUTES.MY_DRIVERS &&
      useSystemModulesAuth &&
      isDriverOnlyProfile(permissionSet?.ROLES ?? [])
    ) {
      return false;
    }

    const userPermission = permissionSet?.SYSTEM_MODULES;
    const hasPermission =
      item?.hasPermission && userPermission.some(item.hasPermission);
    const hasFlag = flags[item.flag]?.enabled;

    return flag_for_access_management.enabled
      ? hasPermission && hasFlag
      : hasFlag;
  });

  return (
    <List>
      {filteredMenuListUser.map((item, index) => (
        <SidebarMenuItem
          key={index}
          item={item}
          classes={classes}
          sidebarOpen={sidebarOpen}
        />
      ))}
    </List>
  );
};

export default ListDrawerUser;
